import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Modal,
  Spinner,
  TabItem,
  Table,
  Tabs,
  Timeline,
} from "flowbite-react";
import ReactApexChart from "react-apexcharts";
import { ADMIN, API, BANNERS, PAYMENTS, QUERIES } from "../config/apiConfig";
import { useQuery } from "react-query";
import axios from "../config/axiosInstance";
import moment from "moment";
import { Link } from "react-router-dom";
import { formatToReal } from "../utils/utils";
import DrawerLoading from "./DrawerLoading";
import {
  getBannerStateKey,
  TARGET_LOOKUP,
  formatDateTime,
} from "./banners/BannerCard";
import { MdOutlineManageSearch, MdOutlineMoneyOff } from "react-icons/md";
import { HiOutlineMail, HiOutlinePhone } from "react-icons/hi";
import { useApplication } from "../contexts/ApplicationContext";
import { GiReceiveMoney } from "react-icons/gi";
import { IoIosPerson, IoMdBusiness } from "react-icons/io";
import { motion } from "framer-motion";
import {
  FaEye,
  FaEyeSlash,
  FaMoneyBills,
  FaRankingStar,
} from "react-icons/fa6";
import { CopyBlock, dracula } from "react-code-blocks";
import { Tooltip } from "react-tooltip";
import { GoLinkExternal } from "react-icons/go";
import QuickActions from "./QuickActions";
import {
  FaBolt,
  FaBullseye,
  FaCalendarDay,
  FaChartBar,
  FaChartLine,
  FaInfoCircle,
  FaMedal,
  FaMoneyBillWave,
  FaPlus,
  FaPlusCircle,
  FaSearch,
  FaUserCheck,
} from "react-icons/fa";
import { BsSearch } from "react-icons/bs";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import { Pagination } from "swiper/modules";
import ModalAddCredit from "./modals/ModalAddCredit";
import { HiOutlineCursorClick } from "react-icons/hi";
import { LuTrophy } from "react-icons/lu";

import { FiArrowLeft, FiArrowRight } from "react-icons/fi";

const AUTOPLAY_DURATION = 5000;
const PROGRESS_INTERVAL = 100;
const PROGRESS_STEP = (PROGRESS_INTERVAL / AUTOPLAY_DURATION) * 100;

const MANAGER_INSIGHTS_SKELETON_VARIANTS = {
  growth: {
    itemContainer: "border-emerald-100 bg-emerald-50/50",
    badge: "bg-emerald-200",
    lineStrong: "bg-emerald-200",
    lineSoft: "bg-emerald-100",
    statStrong: "bg-emerald-300",
  },
  risk: {
    itemContainer: "border-rose-100 bg-rose-50/50",
    badge: "bg-rose-200",
    lineStrong: "bg-rose-200",
    lineSoft: "bg-rose-100",
    statStrong: "bg-rose-300",
  },
  cohort: {
    itemContainer: "border-indigo-100 bg-indigo-50/50",
    badge: "bg-indigo-200",
    lineStrong: "bg-indigo-200",
    lineSoft: "bg-indigo-100",
    statStrong: "bg-indigo-300",
  },
};

const ManagerInsightsCardSkeleton = ({ variant = "growth" }) => {
  const styles =
    MANAGER_INSIGHTS_SKELETON_VARIANTS[variant] ||
    MANAGER_INSIGHTS_SKELETON_VARIANTS.growth;

  return (
    <div className="space-y-2 max-h-[350px] overflow-hidden pr-1 animate-pulse">
      {Array.from({ length: 5 }).map((_, index) => (
        <div
          key={`manager-insights-skeleton-${variant}-${index}`}
          className={`rounded-lg border px-3 py-2 ${styles.itemContainer}`}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <div className={`h-7 w-7 rounded-full ${styles.badge}`} />
              <div className="min-w-0 flex-1 space-y-1.5">
                <div
                  className={`h-3 rounded-full w-11/12 ${styles.lineStrong}`}
                />
                <div
                  className={`h-2.5 rounded-full w-7/12 ${styles.lineSoft}`}
                />
              </div>
            </div>
            <div className="w-16 space-y-1.5">
              <div className={`h-3 rounded-full w-full ${styles.statStrong}`} />
              <div
                className={`h-2.5 rounded-full w-3/4 ml-auto ${styles.lineSoft}`}
              />
            </div>
          </div>
          <div className="mt-2 space-y-1.5">
            <div className={`h-2.5 rounded-full w-10/12 ${styles.lineSoft}`} />
            <div className={`h-2.5 rounded-full w-8/12 ${styles.lineSoft}`} />
          </div>
        </div>
      ))}
    </div>
  );
};

const Dashboard = ({ children }) => {
  const { user, userPermission } = useApplication();
  const [isOpenModalAddCredit, setIsOpenModalAddCredit] = useState(false);
  const bannerSwiperRef = useRef(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const [timerProgress, setTimerProgress] = useState(0);
  const timerProgressRef = useRef(0);
  const [showInvoiceToday, setShowInvoiceToday] = useState(false);
  const [showInvoiceMonth, setShowInvoiceMonth] = useState(false);
  const [isOpenMostQueriesModal, setIsOpenMostQueriesModal] = useState(false);
  const [isOpenAccessTodayModal, setIsOpenAccessTodayModal] = useState(false);
  const [isOpenQueriesByDayModal, setIsOpenQueriesByDayModal] = useState(false);
  const [isOpenActivationCohortModal, setIsOpenActivationCohortModal] =
    useState(false);
  const [selectedActivationCohort, setSelectedActivationCohort] =
    useState(null);
  const [queriesByDayFilter, setQueriesByDayFilter] = useState({
    rawDate: "",
    label: "",
    status: "success",
  });
  const [queriesByDayPage, setQueriesByDayPage] = useState(1);
  const {
    isLoading,
    error: errorDashboardInfos,
    data: dashboardInfos,
  } = useQuery(
    ["dashboardInfos"],
    async () => {
      const { data } = await axios.get(`${QUERIES.GET_DASHBOARD_INFOS}`);
      return data.data;
    },
    { staleTime: 60 * 1000, refetchOnWindowFocus: false },
  );

  function getInitials(name) {
    if (!name) return "";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  }

  const {
    isLoading: isLoadingRepresentatives,
    error: errorDashboardInfosRepresentatives,
    data: dashboardInfosRepresentative,
  } = useQuery(
    ["dashboardInfosRepresentative"],
    async () => {
      const { data } = await axios.get(
        `${QUERIES.GET_DASHBOARD_INFOS_REPRESENTATIVE}`,
      );

      return data.data;
    },
    {
      enabled:
        user?.Company?.companyTypeId === "REPRESENTANTE" ||
        userPermission === "SELLER",
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );

  const {
    isLoading: isLoadingPowerUser,
    error: errorDashboardInfosPowerUsers,
    data: dashboardInfosPowerUser,
  } = useQuery(
    ["dashboardInfosPowerUser"],
    async () => {
      const { data } = await axios.get(
        `${QUERIES.GET_DASHBOARD_INFOS_POWER_USER}`,
      );

      return data.data;
    },
    {
      enabled: userPermission === "POWER_USER",
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );

  const {
    isLoading: isLoadingAccessToday,
    error: errorAccessToday,
    data: accessToday,
  } = useQuery(
    ["accessToday"],
    async () => {
      const { data } = await axios.get(`${ADMIN.GET_ACCESS_TODAY}`);

      return data || 0;
    },
    {
      enabled:
        userPermission === "POWER_USER" ||
        userPermission === "SELLER" ||
        user?.Company?.companyTypeId === "REPRESENTANTE",
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );

  const { isLoading: isLoadingQueriesByDay, data: queriesByDayData } = useQuery(
    [
      "dashboardQueriesByDayStatus",
      queriesByDayFilter.rawDate,
      queriesByDayFilter.status,
      queriesByDayPage,
    ],
    async () => {
      const { data } = await axios.get(
        `${QUERIES.GET_DASHBOARD_QUERIES_BY_DAY_STATUS}`,
        {
          params: {
            date: queriesByDayFilter.rawDate,
            status: queriesByDayFilter.status,
            page: queriesByDayPage,
            itemsPerPage: 10,
          },
        },
      );

      return data.data;
    },
    {
      enabled:
        isOpenQueriesByDayModal &&
        !!queriesByDayFilter.rawDate &&
        !!queriesByDayFilter.status,
      keepPreviousData: true,
      staleTime: 30 * 1000,
      refetchOnWindowFocus: false,
    },
  );

  const isAdminUser = Array.isArray(userPermission)
    ? userPermission.includes("ADMIN")
    : userPermission === "ADMIN";
  const isSeller = Array.isArray(userPermission)
    ? userPermission.includes("SELLER")
    : userPermission === "SELLER";
  const isPowerUser = Array.isArray(userPermission)
    ? userPermission.includes("POWER_USER")
    : userPermission === "POWER_USER";
  const isRepAdmin =
    (isAdminUser && user?.Company?.companyTypeId === "REPRESENTANTE") ||
    isSeller;
  const canSeeBoletoAlerts = isRepAdmin || isPowerUser;
  const currentCompanyId = user?.Company?.id ?? user?.companyId ?? null;
  const parentCompanyId = user?.Company?.ParentCompany?.id ?? null;
  const shouldRestrictBoletoAlerts =
    user?.Company?.companyTypeId === "REPRESENTANTE";

  const { isLoading: isLoadingManagerInsights, data: managerInsights } =
    useQuery(
      ["dashboardManagerInsights"],
      async () => {
        const { data } = await axios.get(
          `${QUERIES.GET_DASHBOARD_MANAGER_INSIGHTS}`,
        );
        return data.data;
      },
      {
        enabled: isPowerUser || isRepAdmin,
        staleTime: 60 * 1000,
        refetchOnWindowFocus: false,
      },
    );

  const normalizeId = (value) => (value == null ? null : String(value));
  const sameId = (a, b) => {
    const na = normalizeId(a);
    const nb = normalizeId(b);
    return na != null && nb != null && na === nb;
  };
  const getRepresentativeIdFromInvoice = (inv) => {
    const company = inv?.Company;
    return (
      company?.representativeId ??
      company?.representative?.id ??
      company?.Representative?.id ??
      company?.parentCompanyId ??
      company?.parentId ??
      company?.ParentCompany?.id ??
      inv?.representativeId ??
      inv?.parentCompanyId ??
      null
    );
  };
  const shouldIncludeBoletoAlert = (inv) => {
    const repId = getRepresentativeIdFromInvoice(inv);
    if (parentCompanyId && sameId(repId, parentCompanyId)) return false;
    if (currentCompanyId && repId != null)
      return sameId(repId, currentCompanyId);
    if (currentCompanyId) {
      const companyId = inv?.companyId ?? inv?.Company?.id;
      return sameId(companyId, currentCompanyId);
    }
    return true;
  };

  const { isLoading: isLoadingBoletoAlerts, data: boletoAlerts } = useQuery(
    ["rep-boleto-alerts", user?.Company?.id, user?.companyId],
    async () => {
      const directParam = canSeeBoletoAlerts ? "&directCustomers=true" : "";
      const { data } = await axios.get(
        `${ADMIN.GET_PAYMENTS}?status=0&typePayment=BOLETO&itemsPerPage=40${directParam}`,
      );
      const rows = data?.data?.data || [];
      const scopedRows = shouldRestrictBoletoAlerts
        ? rows.filter(shouldIncludeBoletoAlert)
        : rows;
      const today = moment().startOf("day");
      const overdue = [];
      const upcoming = [];
      scopedRows.forEach((inv) => {
        if (!inv?.dueDate) return;
        const due = moment(inv.dueDate).startOf("day");
        const diff = due.diff(today, "days");
        if (diff < 0) overdue.push({ ...inv, __diff: diff });
        else if (diff <= 7) upcoming.push({ ...inv, __diff: diff });
      });
      overdue.sort((a, b) => a.__diff - b.__diff);
      upcoming.sort((a, b) => a.__diff - b.__diff);
      return { overdue, upcoming };
    },
    {
      enabled: canSeeBoletoAlerts,
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );

  const { isLoading: isLoadingLowBalancePre, data: lowBalancePreCustomers } =
    useQuery(
      ["lowBalancePreCustomers"],
      async () => {
        const { data } = await axios.get(
          `${ADMIN.GET_LOW_BALANCE_PRE_CUSTOMERS}`,
          { params: { maxBalance: 20 } },
        );
        return data?.data || [];
      },
      {
        enabled: isPowerUser,
        staleTime: 60 * 1000,
        refetchOnWindowFocus: false,
      },
    );

  const {
    isLoading: isLoadingInvoicesOpen,
    error: errorInvoicesOpen,
    data: invoicesOpen,
  } = useQuery(
    ["invoicesOpen"],
    async () => {
      const { data } = await axios.get(`${PAYMENTS.OPEN_PAYMENTS}`);

      return data.data;
    },
    { staleTime: 60 * 1000, refetchOnWindowFocus: false },
  );

  const {
    isLoading: isLoadingToken,
    error: errorToken,
    data: token,
  } = useQuery(
    ["token"],
    async () => {
      const data = await axios.get(`${API.GET_TOKEN}`);

      return data.data.token;

      return null;
    },
    {
      enabled: !!user?.Company?.isIntegrator,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );

  const {
    isLoading: isLoadingBanners,
    error: errorBanners,
    data: dashboardBanners = [],
  } = useQuery(
    ["dashboardBanners", user?.Company?.id, user?.Company?.companyId],
    async () => {
      const { data } = await axios.get(`${BANNERS.LIST}`);
      return data.data;
    },
    {
      enabled: !!user,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );

  const activeBanners = useMemo(() => {
    if (!Array.isArray(dashboardBanners)) return [];
    return dashboardBanners.filter(
      (banner) =>
        getBannerStateKey(banner.startDate, banner.endDate) === "active",
    );
  }, [dashboardBanners]);

  const shouldShowBannerSection =
    isLoadingBanners || errorBanners || activeBanners.length > 0;
  const hasMultipleActiveBanners = activeBanners.length > 1;

  useEffect(() => {
    if (!hasMultipleActiveBanners) {
      timerProgressRef.current = 0;
      setTimerProgress(0);
      return;
    }

    timerProgressRef.current = 0;
    setTimerProgress(0);

    const interval = setInterval(() => {
      const nextProgress = timerProgressRef.current + PROGRESS_STEP;
      if (nextProgress >= 100) {
        timerProgressRef.current = 0;
        setTimerProgress(0);
        const swiper = bannerSwiperRef.current;
        if (swiper) {
          const isLastSlide = swiper.realIndex >= activeBanners.length - 1;
          if (isLastSlide) {
            swiper.slideTo(0);
          } else {
            swiper.slideNext();
          }
        }
      } else {
        timerProgressRef.current = nextProgress;
        setTimerProgress(nextProgress);
      }
    }, PROGRESS_INTERVAL);

    return () => clearInterval(interval);
  }, [activeBanners.length, hasMultipleActiveBanners]);

  const remainingSeconds = hasMultipleActiveBanners
    ? Math.max(
        0,
        Math.ceil((AUTOPLAY_DURATION * (1 - timerProgress / 100)) / 1000),
      )
    : 0;

  const queriesHistoryByDay = useMemo(() => {
    return (dashboardInfos?.lastQueriesBy7Days || []).map((obj) => {
      const [, month, day] = obj.date.split("-");
      const parsedSuccess = Number(obj.successQueries);
      const parsedFailed = Number(obj.failedQueries);

      const hasStatusBreakdown =
        Number.isFinite(parsedSuccess) || Number.isFinite(parsedFailed);
      const fallbackTotal = Number(obj.totalQueries || 0);

      return {
        rawDate: obj.date,
        label: `${day}/${month}`,
        success: hasStatusBreakdown ? parsedSuccess || 0 : fallbackTotal,
        failure: hasStatusBreakdown ? parsedFailed || 0 : 0,
      };
    });
  }, [dashboardInfos?.lastQueriesBy7Days]);

  const mostQueriesDaily = dashboardInfos?.mostQueriesDaily || [];
  const mostQueriesDailyPreview = mostQueriesDaily.slice(0, 10);
  const companiesWithAccessToday = useMemo(() => {
    const clients = Array.isArray(accessToday?.clients)
      ? accessToday.clients
      : [];
    return clients
      .filter((company) => Number(company?.accessesToday || 0) > 0)
      .sort(
        (a, b) => Number(b?.accessesToday || 0) - Number(a?.accessesToday || 0),
      );
  }, [accessToday?.clients]);

  const handleOpenQueriesByDayModal = (seriesIndex, dataPointIndex) => {
    const selectedDay = queriesHistoryByDay[dataPointIndex];
    if (!selectedDay) return;

    const status = seriesIndex === 0 ? "success" : "failure";
    const selectedValue =
      status === "success" ? selectedDay.success : selectedDay.failure;

    if (!selectedValue) return;

    setQueriesByDayFilter({
      rawDate: selectedDay.rawDate,
      label: selectedDay.label,
      status,
    });
    setQueriesByDayPage(1);
    setIsOpenQueriesByDayModal(true);
  };

  const handleOpenActivationCohortModal = (cohort) => {
    setSelectedActivationCohort(cohort);
    setIsOpenActivationCohortModal(true);
  };

  const queriesByDayRows = queriesByDayData?.rows || [];
  const queriesByDayPagination = queriesByDayData?.pagination || {
    page: 1,
    totalPages: 1,
    total: 0,
  };
  const queriesByDayStatusLabel =
    queriesByDayFilter.status === "success" ? "Sucesso" : "Falha";
  const growthRanking = managerInsights?.growthRanking || [];
  const atRiskClients = managerInsights?.atRiskClients || [];
  const activationCohort = managerInsights?.activationCohort || [];
  const selectedActivationCohortActivatedClients =
    selectedActivationCohort?.activatedClients || [];
  const selectedActivationCohortNotActivatedClients =
    selectedActivationCohort?.notActivatedClients || [];

  return (
    <>
      {invoicesOpen?.length > 0 && (
        <Alert className="mb-4" color="warning" withBorderAccent>
          <div>
            Atenção! Você possui <b>({invoicesOpen?.length})</b> faturas em
            aberto!{" "}
            <Link to="/invoices" className="underline">
              Clique aqui para visualizar
            </Link>
          </div>
        </Alert>
      )}
      {shouldShowBannerSection &&
        user?.Company?.companyTypeId !== "CLIENTE" &&
        userPermission !== "SELLER" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className=""
          >
            {errorBanners && (
              <Alert className="mt-4" color="failure">
                Não foi possível carregar os banners. Tente novamente mais
                tarde.
              </Alert>
            )}
            {isLoadingBanners ? (
              <div className="h-[60px] md:h-[200px] rounded-xl border border-dashed border-slate-200 bg-white/60 animate-pulse relative">
                <div
                  className="absolute"
                  style={{
                    transform: "translate(-50%,-50%)",
                    left: "50%",
                    top: "50%",
                  }}
                >
                  <Spinner size={"xl"} />
                </div>
              </div>
            ) : activeBanners.length ? (
              <div className="relative -mb-5">
                <Swiper
                  spaceBetween={24}
                  slidesPerView={1}
                  className="h-full"
                  onSwiper={(swiper) => {
                    bannerSwiperRef.current = swiper;
                    setActiveSlide(swiper.realIndex);
                  }}
                  onSlideChange={(swiper) => setActiveSlide(swiper.realIndex)}
                >
                  {activeBanners.map((banner) => (
                    <SwiperSlide
                      key={`dashboard-banner-${banner.id}`}
                      className="flex justify-center w-full"
                    >
                      <div className="relative w-full overflow-hidden rounded-2xl border border-slate-200 bg-slate-900 shadow-md">
                        {banner.imageSignedUrl ? (
                          <img
                            src={banner.imageSignedUrl}
                            alt={banner.title || "Banner ativo"}
                            className="h-[60px] md:h-[200px] w-full object-cover"
                          />
                        ) : (
                          <div className="h-[60px] md:h-[200px] w-full bg-slate-800" />
                        )}

                        {banner.showTitle && (
                          <>
                            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />
                            <div className="absolute inset-x-0 bottom-0 px-8 pb-8 pt-6 text-white">
                              <h3 className="mt-3 text-3xl font-bold leading-tight">
                                {banner.title}
                              </h3>
                              <p className="mt-2 text-sm text-white/80 line-clamp-3">
                                {banner.description}
                              </p>
                            </div>
                          </>
                        )}
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>
                {hasMultipleActiveBanners && (
                  <>
                    <div className="absolute inset-0 pointer-events-none">
                      <div className="absolute bottom-12 left-4 right-4 z-20 pointer-events-none hidden md:block">
                        <div className="h-1 rounded-full bg-white/30">
                          <div
                            className="h-2 rounded-full bg-gradient-to-r from-amber-400 via-rose-500 to-indigo-500 transition-all duration-100"
                            style={{ width: `${timerProgress}%` }}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="absolute top-4 right-3 hidden md:flex items-center gap-4 rounded-full border border-white/30 bg-black/40 px-4 py-2 text-white shadow-xl backdrop-blur z-30">
                      <button
                        type="button"
                        disabled={activeSlide === 0}
                        onClick={() => bannerSwiperRef.current?.slidePrev()}
                        className="flex h-5 w-5 md:h-10 md:w-10 items-center justify-center rounded-full border border-white/40 bg-white/10 transition hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-50"
                        aria-label="Banner anterior"
                      >
                        <FiArrowLeft className="text-base" />
                      </button>
                      <div className="flex flex-col items-center text-[11px] uppercase tracking-[0.3em]">
                        <div className="flex items-center gap-1 text-lg font-semibold">
                          {activeSlide + 1}
                          <span className="text-slate-300">/</span>
                          {activeBanners.length}
                        </div>
                      </div>
                      <button
                        type="button"
                        disabled={activeSlide === activeBanners.length - 1}
                        onClick={() => bannerSwiperRef.current?.slideNext()}
                        className="flex h-5 w-5 md:h-10 md:w-10 items-center justify-center rounded-full border border-white/40 bg-white/10 transition hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-50"
                        aria-label="Próximo banner"
                      >
                        <FiArrowRight className="text-base" />
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="mt-6 rounded-2xl border border-dashed border-slate-300 bg-slate-50 py-12 text-center text-sm text-slate-500">
                Nenhum banner ativo no momento.
              </div>
            )}
          </motion.div>
        )}
      <motion.div
        className="rounded-lg dark:border-gray-700"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        {userPermission === "POWER_USER" && (
          <Swiper
            spaceBetween={16}
            slidesPerView={1.2}
            breakpoints={{
              640: {
                slidesPerView: 2.2,
              },
              1250: {
                slidesPerView: 4,
                allowTouchMove: false,
              },
            }}
            modules={[Pagination]}
            pagination={{ clickable: true }}
          >
            {/* 💰 Receita diária */}
            <SwiperSlide>
              <div className="bg-gradient-to-br from-[#162e73] via-[#1f3d9a] to-[#2749c3] text-white p-6 rounded-xl shadow-lg flex justify-between items-center h-full">
                <div>
                  <p className="text-sm opacity-90">Receber hoje</p>
                  <div className="text-2xl font-bold mt-1 flex items-center gap-2">
                    {showInvoiceToday
                      ? formatToReal(
                          dashboardInfosPowerUser?.totalInvoiceToday || 0,
                        )
                      : "••••"}
                    <button
                      onClick={() => setShowInvoiceToday(!showInvoiceToday)}
                    >
                      {showInvoiceToday ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                  <p className="text-xs opacity-80 mt-1">Faturamento do dia</p>
                </div>
                <FaMoneyBills className="text-white text-3xl opacity-90" />
              </div>
            </SwiperSlide>

            {/* 📆 Receita mensal */}
            <SwiperSlide>
              <div className="bg-gradient-to-br from-[#006D5B] via-[#008978] to-[#00A891] text-white p-6 rounded-xl shadow-lg flex justify-between items-center h-full">
                <div>
                  <p className="text-sm opacity-90">Receber no mês</p>
                  <div className="text-2xl font-bold mt-1 flex items-center gap-2">
                    {showInvoiceMonth
                      ? formatToReal(
                          dashboardInfosPowerUser?.totalInvoiceMonth || 0,
                        )
                      : "••••"}
                    <button
                      onClick={() => setShowInvoiceMonth(!showInvoiceMonth)}
                    >
                      {showInvoiceMonth ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                  <p className="text-xs opacity-80 mt-1">Faturamento mensal</p>
                </div>
                <GiReceiveMoney className="text-white text-3xl opacity-90" />
              </div>
            </SwiperSlide>

            {/* 👥 Clientes ativos */}
            <SwiperSlide>
              <div className="bg-gradient-to-br from-[#f0f6ff] via-[#e2ecfb] to-[#d4e4f7] border border-blue-200 p-6 rounded-xl shadow-sm hover:shadow-md transition h-full">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-gray-600">Clientes ativos</p>
                    <p className="text-2xl font-bold text-gray-800 mt-1">
                      {dashboardInfosPowerUser?.totalCustomerQuantity ?? 0}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Contas em operação
                    </p>
                  </div>
                  <IoIosPerson className="text-blue-500 text-3xl" />
                </div>
              </div>
            </SwiperSlide>

            <SwiperSlide>
              <div
                className="bg-gradient-to-br from-[#f0f6ff] via-[#e2ecfb] to-[#d4e4f7] border border-blue-200 p-6 rounded-xl shadow-sm hover:shadow-md transition h-full relative cursor-pointer"
                onClick={() => setIsOpenAccessTodayModal(true)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setIsOpenAccessTodayModal(true);
                  }
                }}
              >
                {isLoadingAccessToday && <DrawerLoading />}
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-gray-600">Acessos hoje</p>
                    <p className="text-2xl font-bold text-gray-800 mt-1">
                      {accessToday?.totalAccesses || 0}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Usuários únicos no sistema
                    </p>
                  </div>
                  <HiOutlineCursorClick className="text-blue-500 text-3xl" />
                </div>
              </div>
            </SwiperSlide>
          </Swiper>
        )}

        {isRepAdmin && (
            <Swiper
              spaceBetween={16}
              slidesPerView={1.2}
              breakpoints={{
                640: {
                  slidesPerView: 2.2,
                },
                1250: {
                  slidesPerView: 4,
                  allowTouchMove: false,
                },
              }}
              modules={[Pagination]}
              pagination={{ clickable: true }}
            >
              {!isSeller && (
                <>
                  {/* 💰 Receita diária */}
                  <SwiperSlide>
                    <div className="bg-gradient-to-br from-[#162e73] via-[#1f3d9a] to-[#2749c3] text-white p-6 rounded-xl shadow-lg flex justify-between items-center h-full">
                      <div>
                        <p className="text-sm opacity-90">Receber hoje</p>
                        <div className="text-2xl font-bold mt-1 flex items-center gap-2">
                          {showInvoiceToday
                            ? formatToReal(
                                dashboardInfosRepresentative?.totalInvoiceToday ||
                                  0,
                              )
                            : "••••"}
                          <button
                            onClick={() => setShowInvoiceToday(!showInvoiceToday)}
                          >
                            {showInvoiceToday ? <FaEyeSlash /> : <FaEye />}
                          </button>
                        </div>
                        <p className="text-xs opacity-80 mt-1">
                          Faturamento do dia
                        </p>
                      </div>
                      <FaMoneyBills className="text-white text-3xl opacity-90" />
                    </div>
                  </SwiperSlide>

                  {/* 📆 Receita mensal */}
                  <SwiperSlide>
                    <div className="bg-gradient-to-br from-[#006D5B] via-[#008978] to-[#00A891] text-white p-6 rounded-xl shadow-lg flex justify-between items-center h-full">
                      <div>
                        <p className="text-sm opacity-90">Receber no mês</p>
                        <div className="text-2xl font-bold mt-1 flex items-center gap-2">
                          {showInvoiceMonth
                            ? formatToReal(
                                dashboardInfosRepresentative?.totalInvoiceMonth ||
                                  0,
                              )
                            : "••••"}
                          <button
                            onClick={() => setShowInvoiceMonth(!showInvoiceMonth)}
                          >
                            {showInvoiceMonth ? <FaEyeSlash /> : <FaEye />}
                          </button>
                        </div>
                        <p className="text-xs opacity-80 mt-1">
                          Faturamento mensal
                        </p>
                      </div>
                      <GiReceiveMoney className="text-white text-3xl opacity-90" />
                    </div>
                  </SwiperSlide>
                </>
              )}

              {/* 👥 Clientes ativos */}
              <SwiperSlide>
                <div className="bg-gradient-to-br from-[#f0f6ff] via-[#e2ecfb] to-[#d4e4f7] border border-blue-200 p-6 rounded-xl shadow-sm hover:shadow-md transition h-full">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-gray-600">Clientes ativos</p>
                      <p className="text-2xl font-bold text-gray-800 mt-1">
                        {dashboardInfosRepresentative?.totalCustomerQuantity ??
                          0}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Contas em operação
                      </p>
                    </div>
                    <IoIosPerson className="text-blue-500 text-3xl" />
                  </div>
                </div>
              </SwiperSlide>

              <SwiperSlide>
                <div
                  className="bg-gradient-to-br from-[#f0f6ff] via-[#e2ecfb] to-[#d4e4f7] border border-blue-200 p-6 rounded-xl shadow-sm hover:shadow-md transition h-full relative cursor-pointer"
                  onClick={() => setIsOpenAccessTodayModal(true)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setIsOpenAccessTodayModal(true);
                    }
                  }}
                >
                  {isLoadingAccessToday && <DrawerLoading />}
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-gray-600">Acessos hoje</p>
                      <p className="text-2xl font-bold text-gray-800 mt-1">
                        {accessToday?.totalAccesses || 0}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Usuários únicos no sistema
                      </p>
                    </div>
                    <HiOutlineCursorClick className="text-blue-500 text-3xl" />
                  </div>
                </div>
              </SwiperSlide>
            </Swiper>
          )}

        {(userPermission === "POWER_USER" ||
          user?.Company?.companyTypeId === "REPRESENTANTE" ||
          (user?.companyId == 1 && userPermission === "ADMIN") ||
          isSeller) && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 gap-4 mb-4">
              {user?.Company?.companyTypeId === "CLIENTE" && !isSeller ? (
                <Card className="card-dashboard relative">
                  <Tabs aria-label="Default tabs" variant="default">
                    <TabItem active title="Informações">
                      <div className="flex flex-col gap-4 text-sm text-gray-700">
                        {/* Plano contratado */}
                        <div className="flex items-center gap-2">
                          <span className="text-lg">📦</span>
                          <span className="text-base">Plano:</span>
                          <span className="rounded-md bg-primary text-white text-xs px-2 py-1 font-semibold">
                            {user?.Company?.typePayment === "POS"
                              ? "Pós-Pago"
                              : "Pré-Pago"}
                          </span>
                        </div>

                        {/* Data de cadastro */}
                        <div className="flex items-center gap-2">
                          <span className="text-lg">📅</span>
                          <span className="text-base">Data de cadastro:</span>
                          <span className="font-semibold">
                            {moment(user?.Company?.createdAt).format(
                              "DD/MM/YYYY",
                            )}
                          </span>
                        </div>

                        {/* Se pré-pago: saldo */}
                        {user?.Company?.typePayment === "PRE" && (
                          <>
                            <div className="flex items-center gap-2">
                              <span className="text-lg">💳</span>
                              <span className="text-base">Saldo:</span>
                              <span className="font-semibold">
                                {formatToReal(user?.Company?.credit)}
                              </span>
                            </div>

                            {user?.Company?.credit <= 10 &&
                              user?.Company?.credit > 0 && (
                                <Alert color="warning" withBorderAccent>
                                  Seu saldo está acabando. Faça uma recarga para
                                  continuar consultando.
                                </Alert>
                              )}

                            {user?.Company?.credit === 0 && (
                              <Alert color="failure" withBorderAccent>
                                Seu saldo acabou! É necessário recarregar para
                                continuar.
                              </Alert>
                            )}
                          </>
                        )}

                        {/* Se pós-pago: VCM e limite */}
                        {user?.Company?.typePayment === "POS" && (
                          <>
                            {user?.Company?.vcm > 0 && (
                              <div className="flex items-center gap-2">
                                <span className="text-lg">📈</span>
                                <span className="text-base">
                                  Consumação Mímima/Mês:
                                </span>
                                <span className="font-semibold">
                                  {formatToReal(user?.Company?.vcm)}
                                </span>
                              </div>
                            )}

                            {user?.Company?.limitUse > 0 && (
                              <div className="flex items-center gap-2">
                                <span className="text-lg">🚫</span>
                                <span className="text-base">
                                  Limite máximo de uso:
                                </span>
                                <span className="font-semibold">
                                  {formatToReal(user?.Company?.limitUse)}
                                </span>
                              </div>
                            )}

                            {+user?.Company?.limitUse !== 0 &&
                              +user?.Company?.limitUse -
                                +dashboardInfos?.totalPriceUsedMonth <=
                                15 &&
                              +user?.Company?.limitUse -
                                +dashboardInfos?.totalPriceUsedMonth >
                                0 && (
                                <Alert color="warning" withBorderAccent>
                                  Você está se aproximando do seu limite mensal
                                  de uso.
                                </Alert>
                              )}

                            {+user?.Company?.limitUse !== 0 &&
                              +user?.Company?.limitUse -
                                +dashboardInfos?.totalPriceUsedMonth <=
                                0 && (
                                <Alert color="failure" withBorderAccent>
                                  Você atingiu seu limite mensal.
                                </Alert>
                              )}
                          </>
                        )}

                        {/* Integração */}
                        {user?.Company?.isIntegrator && (
                          <div className="mt-3">
                            <div className="font-semibold text-sm mb-1">
                              🔗 Token de integração:
                            </div>
                            {token && (
                              <CopyBlock
                                text={token}
                                theme={dracula}
                                codeBlock
                              />
                            )}
                          </div>
                        )}
                      </div>
                    </TabItem>

                    {user?.Company?.typePayment === "POS" && (
                      <TabItem
                        title={`Pagamentos (${dashboardInfos?.totalPendingExpiredInvoices?.length})`}
                      >
                        <div
                          className="overflow-y-auto"
                          style={{ maxHeight: "300px" }}
                        >
                          {dashboardInfos?.totalPendingExpiredInvoices?.length >
                          0 ? (
                            <div className="rounded-md overflow-hidden border border-gray-100 mt-3">
                              <Table striped className="w-full">
                                <Table.Head>
                                  <td className="bg-primary py-1 px-1 text-center font-semibold text-2xs text-white">
                                    Tipo
                                  </td>
                                  <td className="bg-primary py-1 px-1 text-center font-semibold text-2xs text-white">
                                    Dt. Criação
                                  </td>
                                  <td className="bg-primary py-1 px-1 text-center font-semibold text-2xs text-white">
                                    Dt. Vencimento
                                  </td>
                                  <td className="bg-primary py-1 px-1 text-center font-semibold text-2xs text-white">
                                    Valor
                                  </td>
                                  <td className="bg-primary py-1 px-1 text-center font-semibold text-2xs text-white">
                                    Ações
                                  </td>
                                </Table.Head>
                                <Table.Body>
                                  {dashboardInfos?.totalPendingExpiredInvoices?.map(
                                    (info, index) => (
                                      <tr
                                        key={index}
                                        className={`${
                                          index % 2 === 0
                                            ? "bg-white"
                                            : "bg-gray-100"
                                        }`}
                                      >
                                        <td className="p-1 text-center">
                                          {info?.type}
                                        </td>
                                        <td className="p-1 text-center">
                                          {moment(info?.createdAt).format(
                                            "DD/MM/yyyy",
                                          )}
                                        </td>
                                        <td className="p-1 text-center">
                                          {moment(info?.dueDate).format(
                                            "DD/MM/yyyy",
                                          )}
                                        </td>
                                        <td className="p-1 text-center">
                                          {formatToReal(info?.price)}
                                        </td>
                                        <td className="p-1 text-center">
                                          <Tooltip
                                            id="tooltip-link"
                                            content="Visualizar boleto"
                                          />
                                          <a
                                            data-tooltip-id="tooltip-link"
                                            href={info.paymentLink}
                                            target="_blank"
                                            className="inline-block mt-1"
                                          >
                                            <GoLinkExternal fontSize={16} />
                                          </a>
                                        </td>
                                      </tr>
                                    ),
                                  )}
                                </Table.Body>
                              </Table>
                            </div>
                          ) : (
                            <div className="flex justify-center items-center h-40 md:h-60 text-sm md:text-base font-semibold">
                              <MdOutlineManageSearch
                                fontSize={25}
                                className="mr-2"
                              />{" "}
                              Nenhum pagamento pendente!
                            </div>
                          )}
                        </div>
                      </TabItem>
                    )}
                  </Tabs>

                  <div className="flex items-center justify-center">
                    {isLoading && <DrawerLoading />}
                  </div>
                </Card>
              ) : (
                <div className="card-dashboard relative rounded-xl bg-white shadow-md p-4">
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div className="flex items-center gap-3">
                      <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 text-white p-2 rounded-xl shadow-md">
                        <FaRankingStar className="text-white text-lg" />
                      </div>
                      <div>
                        <h2 className="text-base md:text-lg font-bold text-gray-800 leading-tight">
                          Clientes com Mais Consultas Hoje
                        </h2>
                        <p className="text-xs text-gray-500 font-medium">
                          Ranking atualizado do dia
                        </p>
                      </div>
                    </div>
                    {mostQueriesDaily.length > 0 && (
                      <Button
                        size="xs"
                        color="light"
                        onClick={() => setIsOpenMostQueriesModal(true)}
                        className="whitespace-nowrap"
                      >
                        Ver todos
                      </Button>
                    )}
                  </div>

                  {mostQueriesDaily.length > 0 ? (
                    <div className="mt-3 flex flex-col gap-[6px]">
                      {mostQueriesDailyPreview.map((info, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between border border-gray-200 rounded-md px-3 py-1.5 bg-white hover:border-blue-300 transition-all"
                        >
                          {/* Cliente + Tipo */}
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="w-6 h-6 flex items-center justify-center rounded-full bg-blue-100 text-blue-700 text-[10px] font-bold shrink-0">
                              {getInitials(
                                info.companyTypeCompany === "CPF"
                                  ? info.companyName
                                  : info.companySocialReason,
                              )}
                            </div>

                            <div className="min-w-0 flex flex-col leading-tight">
                              <div>
                                <a
                                  href={`/admin/customers/${info?.companyUuid}`}
                                  className="text-[12px] text-gray-800 font-medium truncate max-w-[250px] uppercase cursor-pointer hover:underline block"
                                  title={
                                    info.companyTypeCompany === "CPF"
                                      ? info.companyName
                                      : info.companySocialReason
                                  }
                                >
                                  {info.companyTypeCompany === "CPF"
                                    ? info.companyName
                                    : info.companySocialReason}
                                </a>
                              </div>

                              <div className="text-[11px] text-gray-500 italic">
                                {info.companyTypePayment === "POS"
                                  ? "Plano pós-pago"
                                  : "Plano pré-pago"}
                              </div>
                            </div>
                          </div>

                          {/* Contagem */}
                          <div className="text-right text-sm font-bold flex gap-2 items-center">
                            {index === 0 ? <LuTrophy /> : null}
                            <div>
                              <span className="text-green-600">
                                {info.successCount || 0}
                              </span>
                              <span className="text-gray-400 mx-1">/</span>
                              <span className="text-red-600">
                                {info.failedCount || 0}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div
                      className="flex flex-col items-center justify-center h-40 md:h-60 rounded-2xl bg-gradient-to-br from-slate-50 via-white to-slate-100 border border-slate-200 text-slate-700 px-6 text-center shadow-sm"
                      style={{ justifyContent: "center" }}
                    >
                      <div className="w-12 h-12 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center shadow-sm">
                        <MdOutlineManageSearch
                          className="text-blue-600"
                          size={26}
                        />
                      </div>
                      <p className="mt-3 text-sm md:text-base font-semibold text-slate-800">
                        Nenhum cliente realizou consultas hoje
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        Quando houver movimentação, ela aparecerá aqui.
                      </p>
                    </div>
                  )}

                  <div className="flex items-center justify-center">
                    {isLoading && <DrawerLoading />}
                  </div>
                </div>
              )}
              <div className="card-dashboard relative rounded-xl bg-white shadow-md p-4">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 text-white p-2 rounded-xl shadow-md">
                    <FaChartLine className="text-white text-lg" />
                  </div>
                  <div>
                    <h2 className="text-base md:text-lg font-bold text-gray-800 leading-tight">
                      Histórico de Consultas
                    </h2>
                    <p className="text-xs text-gray-500 font-medium">
                      Últimos 7 dias
                    </p>
                  </div>
                </div>

                {!isLoading &&
                  dashboardInfos?.lastQueriesBy7Days?.length > 0 && (
                    <ReactApexChart
                      options={{
                        chart: {
                          height: 460,
                          type: "bar",
                          stacked: true,
                          events: {
                            dataPointSelection: function (
                              event,
                              chartContext,
                              config,
                            ) {
                              handleOpenQueriesByDayModal(
                                config.seriesIndex,
                                config.dataPointIndex,
                              );
                            },
                          },
                          toolbar: {
                            show: false,
                          },
                        },
                        plotOptions: {
                          bar: {
                            columnWidth: "45%",
                            dataLabels: {
                              position: "center",
                            },
                          },
                        },
                        colors: ["#16a34a", "#dc2626"],
                        dataLabels: {
                          enabled: true,
                          style: {
                            fontSize: "12px",
                            fontWeight: "bold",
                            colors: ["#ffffff"],
                          },
                          formatter: function (val) {
                            return val === 0 ? "" : val.toLocaleString("pt-BR");
                          },
                          background: {
                            enabled: false,
                          },
                        },
                        legend: {
                          show: true,
                          position: "top",
                        },
                        xaxis: {
                          categories: queriesHistoryByDay.map(
                            (obj) => obj.label,
                          ),
                          labels: {
                            style: {
                              fontSize: "12px",
                            },
                          },
                        },
                      }}
                      series={[
                        {
                          name: "Sucesso",
                          data: queriesHistoryByDay.map((obj) => obj.success),
                        },
                        {
                          name: "Falha",
                          data: queriesHistoryByDay.map((obj) => obj.failure),
                        },
                      ]}
                      type="bar"
                      height={460}
                    />
                  )}

                <div className="flex items-center justify-center">
                  {isLoading && <DrawerLoading />}
                </div>
              </div>
            </div>
            {(isPowerUser || isRepAdmin) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="card-dashboard relative rounded-xl bg-white shadow-md p-4">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-gradient-to-br from-emerald-600 via-emerald-700 to-teal-700 text-white p-2 rounded-xl shadow-md">
                      <FaChartBar className="text-white text-lg" />
                    </div>
                    <div>
                      <h2 className="text-base md:text-lg font-bold text-gray-800 leading-tight">
                        Ranking de crescimento
                      </h2>
                      <p className="text-xs text-gray-500 font-medium">
                        Últimos 30 dias vs 30 anteriores
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2 max-h-[350px] overflow-y-auto pr-1">
                    {!isLoadingManagerInsights && growthRanking.length > 0
                      ? growthRanking.map((item, index) => (
                          <div
                            key={`${item.companyId}-${index}`}
                            className="flex items-center justify-between rounded-lg border border-emerald-100 bg-emerald-50/40 px-3 py-2"
                          >
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-gray-800 truncate">
                                {item.companyName}
                              </p>
                              <p className="text-[11px] text-gray-500 truncate">
                                Rep: {item.representativeName || "-"}
                              </p>
                            </div>
                            <div className="text-right">
                              <div className="text-sm font-bold text-emerald-700">
                                {item.growthAbsolute >= 0 ? "+" : ""}
                                {item.growthAbsolute}
                              </div>
                              <div className="text-[11px] text-gray-500">
                                {item.growthPercent.toFixed(1)}%
                              </div>
                            </div>
                          </div>
                        ))
                      : !isLoadingManagerInsights && (
                          <div className="text-center py-10 text-slate-600">
                            Sem dados de crescimento no período.
                          </div>
                        )}
                  </div>
                  {isLoadingManagerInsights && (
                    <ManagerInsightsCardSkeleton variant="growth" />
                  )}
                </div>

                <div className="card-dashboard relative rounded-xl bg-white shadow-md p-4">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-gradient-to-br from-rose-600 via-rose-700 to-red-700 text-white p-2 rounded-xl shadow-md">
                      <FaBullseye className="text-white text-lg" />
                    </div>
                    <div>
                      <h2 className="text-base md:text-lg font-bold text-gray-800 leading-tight">
                        Clientes em risco
                      </h2>
                      <p className="text-xs text-gray-500 font-medium">
                        Queda de uso e/ou falhas elevadas
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2 max-h-[350px] overflow-y-auto pr-1">
                    {!isLoadingManagerInsights && atRiskClients.length > 0
                      ? atRiskClients.map((item, index) => (
                          <div
                            key={`${item.companyId}-${index}`}
                            className="rounded-lg border border-rose-100 bg-rose-50/40 px-3 py-2"
                          >
                            <div className="flex items-center justify-between gap-3">
                              <p className="text-sm font-semibold text-gray-800 truncate">
                                {item.companyName}
                              </p>
                            </div>
                            <p className="text-[11px] text-gray-600 mt-1">
                              7d: {item.last7Total} consultas,{" "}
                              {item.failureRate.toFixed(1)}% falha
                            </p>
                            <p className="text-[11px] text-gray-500">
                              Rep: {item.representativeName || "-"}
                            </p>
                          </div>
                        ))
                      : !isLoadingManagerInsights && (
                          <div className="text-center py-10 text-slate-600">
                            Nenhum cliente crítico agora.
                          </div>
                        )}
                  </div>
                  {isLoadingManagerInsights && (
                    <ManagerInsightsCardSkeleton variant="risk" />
                  )}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-4">
              <div className="card-dashboard relative rounded-xl bg-white shadow-md p-4">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-gradient-to-br from-indigo-600 via-indigo-700 to-blue-700 text-white p-2 rounded-xl shadow-md">
                    <FaCalendarDay className="text-white text-lg" />
                  </div>
                  <div>
                    <h2 className="text-base md:text-lg font-bold text-gray-800 leading-tight">
                      Cohort de ativação
                    </h2>
                    <p className="text-xs text-gray-500 font-medium">
                      Novos clientes e ativação em até 7 dias
                    </p>
                  </div>
                </div>

                <div className="space-y-2 max-h-[350px] overflow-y-auto pr-1">
                  {!isLoadingManagerInsights && activationCohort.length > 0
                    ? activationCohort.map((cohort) => (
                        <div
                          key={`${cohort.cohortKey}`}
                          className="rounded-lg border border-indigo-100 bg-indigo-50/40 px-3 py-2 cursor-pointer hover:border-indigo-300 transition"
                          onClick={() => handleOpenActivationCohortModal(cohort)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleOpenActivationCohortModal(cohort);
                            }
                          }}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-sm font-semibold text-gray-800">
                              Semana {moment(cohort.cohortDate).format("DD/MM")}
                            </span>
                            <span className="text-sm font-bold text-indigo-700">
                              {cohort.activationRate.toFixed(1)}%
                            </span>
                          </div>
                          <p className="text-[11px] text-gray-600 mt-1">
                            {cohort.activatedIn7Days}/{cohort.newClients}{" "}
                            ativados em 7 dias
                          </p>
                          <p className="text-[11px] text-indigo-600 mt-1 font-medium">
                            Clique para ver clientes ativados e não ativados
                          </p>
                        </div>
                      ))
                    : !isLoadingManagerInsights && (
                        <div className="text-center py-10 text-slate-600">
                          Sem dados de cohort no período.
                        </div>
                      )}
                </div>
                {isLoadingManagerInsights && (
                  <ManagerInsightsCardSkeleton variant="cohort" />
                )}
              </div>
              <div className="card-dashboard relative rounded-xl bg-white shadow-md p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 text-white p-2 rounded-xl shadow-md">
                      <FaRankingStar className="text-white text-lg" />
                    </div>
                    <div>
                      <h2 className="text-base md:text-lg font-bold text-gray-800 leading-tight">
                        Produtos mais consultados
                      </h2>
                      <p className="text-xs text-gray-500 font-medium">
                        No mês
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  {dashboardInfos?.mostSearchedProductsMonth?.length > 0 ? (
                    dashboardInfos.mostSearchedProductsMonth
                      ?.slice(0, 5)
                      ?.map((query, index) => {
                        const getRankingIcon = () => {
                          const baseStyle =
                            "w-10 h-10 rounded-lg flex items-center justify-center shrink-0";
                          switch (index) {
                            case 0:
                              return (
                                <div className={`${baseStyle} bg-yellow-100`}>
                                  <FaMedal
                                    className="text-yellow-500"
                                    title="1º lugar"
                                  />
                                </div>
                              );
                            case 1:
                              return (
                                <div className={`${baseStyle} bg-gray-200`}>
                                  <FaMedal
                                    className="text-gray-500"
                                    title="2º lugar"
                                  />
                                </div>
                              );
                            case 2:
                              return (
                                <div className={`${baseStyle} bg-orange-100`}>
                                  <FaMedal
                                    className="text-orange-500"
                                    title="3º lugar"
                                  />
                                </div>
                              );
                            default:
                              return (
                                <div className={`${baseStyle} bg-blue-100`}>
                                  <span className="text-blue-700 font-bold text-sm">
                                    {index + 1}
                                  </span>
                                </div>
                              );
                          }
                        };

                        return (
                          <div
                            key={index}
                            className="flex flex-col md:flex-row md:items-center gap-2 justify-between rounded-xl border border-gray-200 bg-white shadow-sm px-4 py-3 hover:shadow-md transition-shadow"
                          >
                            <div className="flex items-center gap-3">
                              {getRankingIcon()}
                              <div className="text-sm font-medium text-gray-800 w-full max-w-[200px] truncate">
                                {query.name}
                              </div>
                            </div>
                            <div className="text-sm font-semibold text-gray-700">
                              {query.totalQueries.toLocaleString("pt-BR")}{" "}
                              {query.totalQueries === 1
                                ? "consulta"
                                : "consultas"}
                            </div>
                          </div>
                        );
                      })
                  ) : (
                    <div className="text-center my-20 font-semibold flex flex-col gap-2 items-center text-gray-600">
                      <MdOutlineManageSearch
                        fontSize={32}
                        className="text-blue-600"
                      />
                      <div>Nenhum produto consultado</div>
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-center">
                  {isLoading && <DrawerLoading />}
                </div>
              </div>
              <div className="card-dashboard relative rounded-xl bg-white shadow-md p-4">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-gradient-to-br from-rose-500 via-amber-500 to-yellow-400 text-white p-2 rounded-xl shadow-md">
                    <FaMoneyBillWave className="text-white text-lg" />
                  </div>
                  <div>
                    <h2 className="text-base md:text-lg font-bold text-gray-800 leading-tight">
                      Boletos em aberto
                    </h2>
                    <p className="text-xs text-gray-500 font-medium">
                      Atrasados e próximos a vencer (até 7 dias)
                    </p>
                  </div>
                </div>

                <div
                  className="overflow-y-auto pr-2 flex flex-col gap-2"
                  style={{ maxHeight: "380px", minHeight: "200px" }}
                >
                  {isLoadingBoletoAlerts && <DrawerLoading />}
                  {!isLoadingBoletoAlerts &&
                  (boletoAlerts?.overdue?.length ||
                    boletoAlerts?.upcoming?.length) ? (
                    <>
                      {boletoAlerts.overdue?.slice(0, 6).map((inv) => {
                        const companyName =
                          inv?.Company?.name ||
                          inv?.Company?.socialReason ||
                          "Empresa";
                        const due = moment(inv.dueDate).format("DD/MM");
                        const days = Math.abs(inv.__diff);
                        return (
                          <div
                            key={`overdue-${inv.id}`}
                            className="bg-white/95 rounded-lg shadow-sm border border-rose-200/70 px-4 py-3 flex items-center justify-between gap-3 transition border-l-4 border-l-rose-500"
                          >
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-gray-800 truncate">
                                {companyName}
                              </p>
                              <p className="text-xs text-rose-600">
                                Atrasado há {days} dia{days === 1 ? "" : "s"} •{" "}
                                {due}
                              </p>
                            </div>
                            <div className="text-right shrink-0">
                              <div className="text-sm font-semibold text-rose-700">
                                {formatToReal(inv.price)}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      {boletoAlerts.upcoming?.slice(0, 6).map((inv) => {
                        const companyName =
                          inv?.Company?.name ||
                          inv?.Company?.socialReason ||
                          "Empresa";
                        const due = moment(inv.dueDate).format("DD/MM");
                        const days = inv.__diff;
                        return (
                          <div
                            key={`upcoming-${inv.id}`}
                            className="bg-white/95 rounded-lg shadow-sm border border-amber-200/70 px-4 py-3 flex items-center justify-between gap-3 transition border-l-4 border-l-amber-400"
                          >
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-gray-800 truncate">
                                {companyName}
                              </p>
                              <p className="text-xs text-amber-700">
                                Vence em {days} dia{days === 1 ? "" : "s"} •{" "}
                                {due}
                              </p>
                            </div>
                            <div className="text-right shrink-0">
                              <div className="text-sm font-semibold text-amber-700">
                                {formatToReal(inv.price)}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </>
                  ) : (
                    !isLoadingBoletoAlerts && (
                      <div className="text-center w-full mt-5 text-gray-600">
                        Nenhum boleto atrasado ou próximo a vencer.
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>

            {isPowerUser && (
              <div className="grid grid-cols-1 gap-4 mb-4">
                <div className="card-dashboard relative rounded-xl bg-white shadow-md p-4">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-gradient-to-br from-rose-600 via-rose-700 to-red-700 text-white p-2 rounded-xl shadow-md">
                      <MdOutlineMoneyOff className="text-white text-lg" />
                    </div>
                    <div>
                      <h2 className="text-base md:text-lg font-bold text-gray-800 leading-tight">
                        Clientes PRE-PAGO (Saldo baixo)
                      </h2>
                      <p className="text-xs text-gray-500 font-medium">
                        Saldo abaixo de R$ 20,00
                      </p>
                    </div>
                  </div>

                  <div
                    className="overflow-y-auto pr-2 flex flex-col gap-2"
                    style={{ maxHeight: "380px", minHeight: "120px" }}
                  >
                    {isLoadingLowBalancePre && <DrawerLoading />}
                    {!isLoadingLowBalancePre &&
                    lowBalancePreCustomers?.length > 0 ? (
                      lowBalancePreCustomers.map((customer) => (
                        <div
                          key={customer.id}
                          className="bg-white/95 rounded-lg shadow-sm border border-rose-200/70 px-4 py-3 flex items-center justify-between gap-3 transition border-l-4 border-l-rose-500"
                        >
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-800 truncate">
                              {customer.name || customer.socialReason}
                            </p>
                            <p className="text-xs text-gray-500 truncate">
                              {customer.phoneNumber || "Sem telefone"}
                              {customer.email ? ` • ${customer.email}` : ""}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <div className="text-sm font-semibold text-rose-700">
                              {formatToReal(customer.credit)}
                            </div>
                            {customer.phoneNumber && (
                              <a
                                href={`https://wa.me/55${String(
                                  customer.phoneNumber,
                                ).replace(/\D/g, "")}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center justify-center h-8 w-8 rounded-full border border-gray-200 hover:bg-gray-50"
                              >
                                <HiOutlinePhone className="text-gray-600" />
                              </a>
                            )}
                            {customer.email && (
                              <a
                                href={`mailto:${customer.email}`}
                                className="flex items-center justify-center h-8 w-8 rounded-full border border-gray-200 hover:bg-gray-50"
                              >
                                <HiOutlineMail className="text-gray-600" />
                              </a>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      !isLoadingLowBalancePre && (
                        <div className="text-center w-full mt-5 text-gray-600">
                          Nenhum cliente direto PRE-PAGO com saldo abaixo de R$
                          20,00.
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {!isSeller &&
          (user?.Company?.companyTypeId === "CLIENTE" ||
            (user?.companyId == 1 && userPermission === "USER")) && (
          <div className="relative overflow-hidden rounded-xl border border-slate-200 bg-gradient-to-br from-white via-slate-50 to-slate-100 p-4 shadow-md text-slate-900">
            <div className="absolute inset-0 bg-gradient-to-br from-slate-900/10 via-slate-800/5 to-transparent" />
            <div
              className="absolute inset-0 opacity-80"
              style={{
                background:
                  "radial-gradient(circle at top right, rgba(59,130,246,0.25), transparent 35%)",
              }}
            />
            <div className="relative z-10 grid gap-5 lg:grid-cols-[1fr_auto] lg:items-center">
              <div className="space-y-2">
                <p className="text-xs uppercase tracking-[0.5em] text-slate-500">
                  Consulte com segurança
                </p>
                <h1 className="text-2xl font-semibold leading-snug text-slate-900 md:text-3xl">
                  Olá,{" "}
                  <span className="text-blue-900">
                    {user?.name || "Gestor(a)"}
                  </span>
                </h1>
                <div className="flex flex-wrap gap-3 text-[10px] uppercase tracking-[0.4em] text-slate-500">
                  {[
                    "Crédito",
                    "Cadastral",
                    "Ações e Processos",
                    "Dívidas tributárias",
                  ].map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full border border-slate-300 bg-white shadow-sm"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {user?.Company?.typePayment === "PRE" && (
                <div className="flex flex-col gap-2 rounded-3xl border border-slate-200 bg-white/80 p-4 text-sm text-slate-900 shadow-lg backdrop-blur">
                  <p className="text-[10px] uppercase tracking-[0.4em] text-slate-500">
                    Saldo disponível
                  </p>
                  <strong className="text-2xl">
                    {formatToReal(user?.Company?.credit)}
                  </strong>
                  <button
                    onClick={() => setIsOpenModalAddCredit(true)}
                    className="rounded-full border border-slate-300 bg-slate-900 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-white shadow-md transition hover:border-slate-500 hover:bg-slate-800"
                  >
                    Recarregar
                  </button>
                </div>
              )}
            </div>

            {shouldShowBannerSection && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="mt-2"
              >
                {errorBanners && (
                  <Alert className="mt-4" color="failure">
                    Não foi possível carregar os banners. Tente novamente mais
                    tarde.
                  </Alert>
                )}
                {isLoadingBanners ? (
                  <div className="h-[60px] md:h-[200px] rounded-xl border border-dashed border-slate-200 bg-white/60 animate-pulse relative">
                    <div
                      className="absolute"
                      style={{
                        transform: "translate(-50%,-50%)",
                        left: "50%",
                        top: "50%",
                      }}
                    >
                      <Spinner size={"xl"} />
                    </div>
                  </div>
                ) : activeBanners.length ? (
                  <div className="relative -mb-10 rounded-3xl">
                    <Swiper
                      spaceBetween={24}
                      slidesPerView={1}
                      className="h-full"
                      onSwiper={(swiper) => {
                        bannerSwiperRef.current = swiper;
                        setActiveSlide(swiper.realIndex);
                      }}
                      onSlideChange={(swiper) =>
                        setActiveSlide(swiper.realIndex)
                      }
                    >
                      {activeBanners.map((banner) => (
                        <SwiperSlide
                          key={`dashboard-banner-${banner.id}`}
                          className="flex justify-center w-full mt-2"
                        >
                          <div className="relative w-full overflow-hidden rounded-2xl bg-slate-900 shadow-md">
                            {banner.imageSignedUrl ? (
                              <img
                                src={banner.imageSignedUrl}
                                alt={banner.title || "Banner ativo"}
                                className=" w-full object-cover banner-swiper-image"
                              />
                            ) : (
                              <div className=" w-full bg-slate-800 banner-swiper-image" />
                            )}

                            {banner.showTitle && (
                              <>
                                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent" />
                                <div className="absolute inset-x-0 bottom-0 px-8 pb-8 pt-6 text-white">
                                  <h3 className="mt-3 text-3xl font-bold leading-tight">
                                    {banner.title}
                                  </h3>
                                  <p className="mt-2 text-sm text-white/80 line-clamp-3">
                                    {banner.description}
                                  </p>
                                </div>
                              </>
                            )}
                          </div>
                        </SwiperSlide>
                      ))}
                    </Swiper>
                    {activeBanners.length > 1 && (
                      <>
                        <div className="absolute inset-0 pointer-events-none">
                          <div className="absolute bottom-12 left-4 right-4 z-20 pointer-events-none hidden md:block">
                            <div className="h-1 rounded-full bg-white/30">
                              <div
                                className="h-2 rounded-full bg-gradient-to-r from-amber-400 via-rose-500 to-indigo-500 transition-all duration-100"
                                style={{ width: `${timerProgress}%` }}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="absolute top-6 right-3 hidden md:flex items-center gap-4 rounded-full border border-slate-300/70 bg-white/80 px-4 py-2 text-slate-900 shadow-lg backdrop-blur z-10">
                          <button
                            type="button"
                            disabled={activeSlide === 0}
                            onClick={() => bannerSwiperRef.current?.slidePrev()}
                            className="flex h-5 w-5 md:h-10 md:w-10 items-center justify-center rounded-full border border-slate-300/70 bg-white text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                            aria-label="Banner anterior"
                          >
                            <FiArrowLeft className="text-base" />
                          </button>
                          <div className="flex flex-col items-center text-[11px] uppercase tracking-[0.3em]">
                            <div className="flex items-center gap-1 text-lg font-semibold">
                              {activeSlide + 1}
                              <span className="text-slate-400">/</span>
                              {activeBanners.length}
                            </div>
                          </div>
                          <button
                            type="button"
                            disabled={activeSlide === activeBanners.length - 1}
                            onClick={() => bannerSwiperRef.current?.slideNext()}
                            className="flex h-5 w-5 md:h-10 md:w-10 items-center justify-center rounded-full border border-slate-300/70 bg-white text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                            aria-label="Próximo banner"
                          >
                            <FiArrowRight className="text-base" />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="mt-6 rounded-2xl border border-dashed border-slate-200 bg-white/70 py-12 text-center text-sm text-slate-500">
                    Nenhum banner ativo no momento.
                  </div>
                )}
              </motion.div>
            )}
            <div className="mt-6">
              <QuickActions company={user?.Company} />
            </div>
          </div>
        )}
      </motion.div>
      <ModalAddCredit
        isOpen={isOpenModalAddCredit}
        setOpenModal={setIsOpenModalAddCredit}
      />

      <Modal
        show={isOpenMostQueriesModal}
        onClose={() => setIsOpenMostQueriesModal(false)}
        size="4xl"
      >
        <Modal.Header className="py-2">
          <div className="pt-1 text-base">
            Clientes com Mais Consultas Hoje ({mostQueriesDaily.length})
          </div>
        </Modal.Header>
        <Modal.Body>
          {mostQueriesDaily.length > 0 ? (
            <div className="max-h-[65vh] overflow-y-auto pr-1 flex flex-col gap-2">
              {mostQueriesDaily.map((info, index) => (
                <div
                  key={`${info.companyUuid || info.companyId || index}-${index}`}
                  className="flex items-center justify-between border border-gray-200 rounded-md px-3 py-2 bg-white hover:border-blue-300 transition-all"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-7 h-7 flex items-center justify-center rounded-full bg-blue-100 text-blue-700 text-[10px] font-bold shrink-0">
                      {getInitials(
                        info.companyTypeCompany === "CPF"
                          ? info.companyName
                          : info.companySocialReason,
                      )}
                    </div>

                    <div className="min-w-0 flex flex-col leading-tight">
                      <a
                        href={`/admin/customers/${info?.companyUuid}`}
                        className="text-[12px] text-gray-800 font-medium truncate max-w-[400px] uppercase cursor-pointer hover:underline block"
                        title={
                          info.companyTypeCompany === "CPF"
                            ? info.companyName
                            : info.companySocialReason
                        }
                      >
                        {info.companyTypeCompany === "CPF"
                          ? info.companyName
                          : info.companySocialReason}
                      </a>

                      <div className="text-[11px] text-gray-500 italic">
                        {info.companyTypePayment === "POS"
                          ? "Plano pós-pago"
                          : "Plano pré-pago"}
                      </div>
                    </div>
                  </div>

                  <div className="text-right text-sm font-bold flex gap-2 items-center">
                    {index === 0 ? <LuTrophy /> : null}
                    <div>
                      <span className="text-green-600">
                        {info.successCount || 0}
                      </span>
                      <span className="text-gray-400 mx-1">/</span>
                      <span className="text-red-600">
                        {info.failedCount || 0}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-10 text-slate-600">
              Nenhum cliente realizou consultas hoje.
            </div>
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={isOpenActivationCohortModal}
        onClose={() => setIsOpenActivationCohortModal(false)}
        size="6xl"
      >
        <Modal.Header className="py-2">
          <div className="pt-1 text-base">
            Cohort de ativação - semana {" "}
            {selectedActivationCohort?.cohortDate
              ? moment(selectedActivationCohort.cohortDate).format("DD/MM")
              : "-"}{" "}
            ({selectedActivationCohort?.activatedIn7Days || 0}/
            {selectedActivationCohort?.newClients || 0} ativados)
          </div>
        </Modal.Header>
        <Modal.Body>
          {selectedActivationCohort ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="rounded-lg border border-emerald-100 bg-emerald-50/30 p-3">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-emerald-700">
                    Ativados em até 7 dias
                  </h3>
                  <Badge color="success">
                    {selectedActivationCohortActivatedClients.length}
                  </Badge>
                </div>

                {selectedActivationCohortActivatedClients.length > 0 ? (
                  <div className="max-h-[52vh] overflow-y-auto flex flex-col gap-2 pr-1">
                    {selectedActivationCohortActivatedClients.map(
                      (company, index) => (
                        <div
                          key={`${company.companyUuid || company.companyId || index}-activated-${index}`}
                          className="rounded-md border border-emerald-100 bg-white px-3 py-2"
                        >
                          {company.companyUuid ? (
                            <a
                              href={`/admin/customers/${company.companyUuid}`}
                              className="text-sm font-semibold text-gray-800 hover:underline uppercase"
                              title={company.companyName}
                            >
                              {company.companyName}
                            </a>
                          ) : (
                            <div className="text-sm font-semibold text-gray-800 uppercase">
                              {company.companyName}
                            </div>
                          )}

                          <p className="text-[11px] text-gray-500 mt-1">
                            Rep: {company.representativeName || "-"}
                          </p>
                          <p className="text-[11px] text-gray-500">
                            Primeiro acesso: {" "}
                            {company.firstQueryAt
                              ? moment(company.firstQueryAt).format(
                                  "DD/MM/YYYY HH:mm",
                                )
                              : "-"}
                          </p>
                        </div>
                      ),
                    )}
                  </div>
                ) : (
                  <div className="text-sm text-slate-600 py-6 text-center">
                    Nenhum cliente ativado neste cohort.
                  </div>
                )}
              </div>

              <div className="rounded-lg border border-rose-100 bg-rose-50/30 p-3">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-rose-700">
                    Não ativados em até 7 dias
                  </h3>
                  <Badge color="failure">
                    {selectedActivationCohortNotActivatedClients.length}
                  </Badge>
                </div>

                {selectedActivationCohortNotActivatedClients.length > 0 ? (
                  <div className="max-h-[52vh] overflow-y-auto flex flex-col gap-2 pr-1">
                    {selectedActivationCohortNotActivatedClients.map(
                      (company, index) => (
                        <div
                          key={`${company.companyUuid || company.companyId || index}-not-activated-${index}`}
                          className="rounded-md border border-rose-100 bg-white px-3 py-2"
                        >
                          {company.companyUuid ? (
                            <a
                              href={`/admin/customers/${company.companyUuid}`}
                              className="text-sm font-semibold text-gray-800 hover:underline uppercase"
                              title={company.companyName}
                            >
                              {company.companyName}
                            </a>
                          ) : (
                            <div className="text-sm font-semibold text-gray-800 uppercase">
                              {company.companyName}
                            </div>
                          )}

                          <p className="text-[11px] text-gray-500 mt-1">
                            Rep: {company.representativeName || "-"}
                          </p>
                          <p className="text-[11px] text-gray-500">
                            Cadastro: {" "}
                            {company.companyCreatedAt
                              ? moment(company.companyCreatedAt).format(
                                  "DD/MM/YYYY",
                                )
                              : "-"}
                          </p>
                        </div>
                      ),
                    )}
                  </div>
                ) : (
                  <div className="text-sm text-slate-600 py-6 text-center">
                    Nenhum cliente não ativado neste cohort.
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-10 text-slate-600">
              Nenhum cohort selecionado.
            </div>
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={isOpenAccessTodayModal}
        onClose={() => setIsOpenAccessTodayModal(false)}
        size="2xl"
      >
        <Modal.Header className="py-2">
          <div className="pt-1 text-base">
            Empresas que acessaram hoje ({companiesWithAccessToday.length})
          </div>
        </Modal.Header>
        <Modal.Body>
          {companiesWithAccessToday.length > 0 ? (
            <div className="max-h-[65vh] overflow-y-auto pr-1 flex flex-col gap-2">
              {companiesWithAccessToday.map((company, index) => (
                <div
                  key={`${company.companyId || company.companyName}-${index}`}
                  className="flex items-center justify-between border border-gray-200 rounded-md px-3 py-2 bg-white hover:border-blue-300 transition-all"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-7 h-7 flex items-center justify-center rounded-full bg-blue-100 text-blue-700 text-[10px] font-bold shrink-0">
                      {getInitials(company.companyName)}
                    </div>
                    <div className="min-w-0 flex flex-col leading-tight">
                      <div
                        className="text-[12px] text-gray-800 font-medium truncate max-w-[450px] uppercase"
                        title={company.companyName}
                      >
                        {company.companyName}
                      </div>
                    </div>
                  </div>

                  <div className="text-right text-sm font-bold text-blue-700">
                    {company.accessesToday || 0}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-10 text-slate-600">
              Nenhuma empresa acessou o sistema hoje.
            </div>
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={isOpenQueriesByDayModal}
        onClose={() => setIsOpenQueriesByDayModal(false)}
        size="6xl"
      >
        <Modal.Header className="py-2">
          <div className="pt-1 text-base">
            Consultas com {queriesByDayStatusLabel} em{" "}
            {queriesByDayFilter.label}
          </div>
        </Modal.Header>
        <Modal.Body>
          {isLoadingQueriesByDay ? (
            <div className="relative h-40">
              <DrawerLoading />
            </div>
          ) : queriesByDayRows.length > 0 ? (
            <>
              <div className="max-h-[60vh] overflow-auto rounded-md border border-gray-200">
                <Table striped>
                  <Table.Head>
                    <Table.HeadCell>Cliente</Table.HeadCell>
                    <Table.HeadCell>Representante</Table.HeadCell>
                    <Table.HeadCell>Parâmetro</Table.HeadCell>
                    <Table.HeadCell>Consulta</Table.HeadCell>
                    <Table.HeadCell>Data/Hora</Table.HeadCell>
                  </Table.Head>
                  <Table.Body className="divide-y">
                    {queriesByDayRows.map((row) => (
                      <Table.Row key={row.id} className="bg-white">
                        <Table.Cell className="font-medium text-gray-900">
                          {row.companyName || "-"}
                        </Table.Cell>
                        <Table.Cell>{row.representativeName || "-"}</Table.Cell>
                        <Table.Cell>{row.parameter || "-"}</Table.Cell>
                        <Table.Cell>{row.queryName || "-"}</Table.Cell>
                        <Table.Cell>
                          {row.createdAt
                            ? moment(row.createdAt).format("DD/MM/YYYY HH:mm")
                            : "-"}
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              </div>

              <div className="mt-4 flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Total: {queriesByDayPagination.total || 0}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="xs"
                    color="light"
                    disabled={queriesByDayPagination.page <= 1}
                    onClick={() =>
                      setQueriesByDayPage((prev) => Math.max(1, prev - 1))
                    }
                  >
                    Anterior
                  </Button>
                  <span className="text-sm text-gray-700">
                    Página {queriesByDayPagination.page} de{" "}
                    {queriesByDayPagination.totalPages}
                  </span>
                  <Button
                    size="xs"
                    color="light"
                    disabled={
                      queriesByDayPagination.page >=
                      queriesByDayPagination.totalPages
                    }
                    onClick={() => setQueriesByDayPage((prev) => prev + 1)}
                  >
                    Próxima
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-10 text-slate-600">
              Nenhuma consulta encontrada para este filtro.
            </div>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default Dashboard;
